import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { AuthenticationService, LoginError, UserService } from 'src/app/core/services';
import { Functions, httpsCallable } from '@angular/fire/functions';

@Component({ templateUrl: './login.component.html' })
export class LoginComponent implements OnInit {
	loginForm: UntypedFormGroup;
	returnUrl: string;
	loginError: string;
	loginMessage: string;
	isDisabled: boolean = false;
	validTenant: boolean = false;
	tenantId: string;

	constructor(
		private functions: Functions,
		private formBuilder: UntypedFormBuilder,
		private route: ActivatedRoute,
		private router: Router,
		private authenticationService: AuthenticationService
	) {
		if (this.authenticationService.currentUserValue) {
			// if already logged in, navigate to previous route stored in localStorage
			const url = localStorage.getItem('_QR_CURRENT_URL');
			if (url && !url.includes('account') && url !== '/') {
				this.router.navigate([url]);
			} else {
				this.router.navigate(['/dashboard']);
			}
		}
		this.authenticationService.loginError.subscribe((err) => {
			if (err === LoginError.UNKNOWN_TENANT) {
				this.loginForm.get('tenant').patchValue('');
				this.loginError = 'VALIDATIONS.error_tenant_does_not_exist';
			}
			if (err === LoginError.INVALID_EMAIL) {
				this.loginForm.get('email').patchValue('');
				this.loginError = 'VALIDATIONS.email';
			}
			if (err === LoginError.INVALID_PASSWORD) {
				this.loginForm.get('password').patchValue('');
				this.loginError = 'VALIDATIONS.wrong_password';
			}
			if (err === LoginError.OTHER) {
				this.loginForm.get('password').patchValue('');
				this.loginError = err;
			}
			if (err === LoginError.CHECK_EMAIL) {
				this.loginForm.get('password').patchValue('');
				this.loginMessage = 'VALIDATIONS.check_email';
			}
			if (err) {
				this.loginForm.markAsUntouched();
				this.loginForm.updateValueAndValidity();
			}
			this.isDisabled = false;
		});
	}

	ngOnInit() {
		this.loginForm = this.formBuilder.group({
			tenant: ['', Validators.required],
			email: ['', Validators.required],
			password: ['', Validators.required],
		});

		// initialize tenant if available in localStorage
		let tenant = localStorage.getItem('_QR_LOGIN_TENANT');

		// get tenant from query params
		this.route.queryParams.subscribe((params) => {
			if (params['tenant']) {
				tenant = params['tenant'];
			}
		});

		if (tenant) {
			this.loginForm.get('tenant').patchValue(tenant);
			this.checkTenant();
		}

		this.returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/';
	}

	get f() {
		return this.loginForm.controls;
	}
	async onPasswordReset() {
		this.disableForm();
		//check if empty
		if (this.f.tenant.value === '') {
			this.authenticationService.newLoginError(LoginError.UNKNOWN_TENANT);
		} else if (this.f.email.value === '') {
			this.authenticationService.newLoginError(LoginError.INVALID_EMAIL);
		} else {
			try {
				//gets tannant id and passes it too sendResetPasswordMail methode
				const getTenantByDisplayNameFunction = httpsCallable(this.functions, 'getTenantByDisplayName');
				const result = await getTenantByDisplayNameFunction({ tenantDisplayName: this.f.tenant.value });

				if (!result.data) {
					this.authenticationService.newLoginError(LoginError.UNKNOWN_TENANT);
					return;
				}

				try {
					await this.authenticationService.sendResetPasswordMail(this.f.email.value, (result.data as any).tenantId).toPromise();
					this.authenticationService.newLoginError(LoginError.CHECK_EMAIL);
				} catch {
					this.authenticationService.newLoginError(LoginError.INVALID_EMAIL);
				}
			} catch {
				this.authenticationService.newLoginError(LoginError.OTHER);
			}
		}
	}

	onSubmit() {
		this.disableForm();
		this.authenticationService.login(this.tenantId, this.f.email.value, this.f.password.value);
	}

	disableForm() {
		this.isDisabled = true;
		this.loginError = null;
		this.loginMessage = null;
	}

	async checkTenant() {
		const tenantControl = this.loginForm.get('tenant');
		try {
			const getTenantByDisplayName = httpsCallable(this.functions, 'getTenantByDisplayName');

			const result = await getTenantByDisplayName({
				tenantDisplayName: this.f.tenant.value.toLowerCase(),
			});

			if (result.data && (result.data as any).tenantId) {
				this.tenantId = (result.data as any).tenantId;

				// Get the current errors on the control
				const currentErrors = tenantControl.errors;

				// If 'invalidTenant' is present in the errors object, remove it
				if (currentErrors && currentErrors['invalidTenant']) {
					delete currentErrors['invalidTenant'];
					// Set the updated errors object (or null if there are no errors left)
					tenantControl.setErrors(Object.keys(currentErrors).length > 0 ? currentErrors : null);
				}

				localStorage.setItem('_QR_LOGIN_TENANT', this.f.tenant.value.toLowerCase());
			} else {
				tenantControl.setErrors({ invalidTenant: true });
				this.tenantId = null;
			}
		} catch (error) {
			console.error('Error checking tenant:', error);
			tenantControl.setErrors({ invalidTenant: true });
			this.tenantId = null;
		}
	}
	uncheckTenant() {
		this.tenantId = null;
	}
}
